import Component from 'vue-class-component'
import moduleStore from '../store/migu';
import Vue from 'vue';
import PageState from '../store/state';
import { toPublishPage } from '../utils/util';
import { MiguConfig } from '../utils/constant';
import CreateDrawer from '@/views/migu/components/createDrawer.vue';
import SubscribePopup from '@/views/migu/components/subscribePopup.vue';
import AgreementDialog from '@/views/migu/components/agreementDialog.vue';
import { toast } from '@/utils/util';
import to from 'await-to-js';

// 未登录/登录失效提示语（与 audioCreating 保持一致）
const notLoginMessage = '请先登录';
const notLoginErrorMessage = 'token错误请重新登录';

interface PlayListItem {
  videoUrl: string;
  coverUrl: string;
  articleId: string;
  description: string;
  styleEN: string;
  speakerSpeed: string;
  speakerName: string;
  speakerStyle: string;
  type?: string; // '2' = 模板页, 其他 = 作品页
}

type SlotPosition = 'prev' | 'current' | 'next';

interface TrackSlot {
  key: string;       // videoUrl，作为 v-for key
  item: PlayListItem;
  position: SlotPosition;
}

/**components**/
@Component({
  components: { CreateDrawer, SubscribePopup, AgreementDialog }
})
@moduleStore.initVuex({
  mutationNames: moduleStore.mutationNames,
  actionNames: moduleStore.actionNames
})
export default class VideoPlayPage extends Vue implements IvuePage<PageState> {
  state = new PageState();
  self: Vue = this;

  // 原始 query 参数
  videoUrl: string;
  coverUrl: string;
  type: string = '1';
  taskId: string = '';
  articleId: string = '';
  isNewYear: boolean = false;
  // 是否启用上下滑动切换视频（query 参数 swipeable='1' 开启）
  swipeable: boolean = false;

  // ============ 创作抽屉（我也试试） ============
  showCreateDrawer: boolean = false;
  showSubscribePopup: boolean = false; // 控制订购弹窗显示
  showAgreementPopup: boolean = false; // 控制协议弹窗显示
  drawerOpenBeforeAgreement: boolean = false; // 弹协议前抽屉是否打开（关闭协议后恢复）
  drawerAgreed: boolean = false; // 协议勾选状态（弹窗点「我已阅读并同意」后置 true，同步进抽屉）
  isSubmiting: boolean = false;
  theme: string = '';
  drawerText: string = '';
  drawerStyleCode: string = '';
  drawerSpeakerName: string = '';
  drawerSpeakerStyleId: number = 0;
  drawerSpeakerStyleName: string = '';
  drawerSpeechSpeed: string = '';

  // 视频列表
  playList: PlayListItem[] = [];
  currentIndex: number = 0;

  // 滑轨位置：0 = 滑到上一个, 1 = 当前, 2 = 滑到下一个
  trackSlot: number = 1;
  noTransition: boolean = false;

  // 播放状态（跨视频延续）
  isPlaying: boolean = false;
  videoProgress: number = 0; // 当前视频播放进度 0~1
  videoCurrentTime: number = 0; // 当前播放时刻（秒）
  videoDuration: number = 0; // 视频总时长（秒）
  isDraggingProgress: boolean = false; // 进度条拖动中
  dragProgressRatio: number = 0; // 拖动中的目标进度 0~1
  isMuted: boolean = true;
  showPlayIcon: boolean = false;
  showBubble: boolean = false;

  // 首次滑动提示
  showSwipeHint: boolean = false;

  // 触摸滑动
  private touchStartY: number = 0;
  private touchStartTime: number = 0;
  private isSwiping: boolean = false;
  private slideThreshold: number = 60;
  private liveDragY: number = 0; // 触摸过程中实时偏移

  private playIconTimer: any = null;
  private bubbleTimer: any = null;
  private swipeHintTimer: any = null;
  private switching: boolean = false;
  private _bubbleShown: boolean = false;
  private _isFirstPlay: boolean = true; // 是否首次播放（只显示一次播放图标）

  get currentItem(): PlayListItem | null {
    return this.playList[this.currentIndex] || null;
  }

  get isPublish() {
    // 跟随当前播放项的类型（playList 未就绪时退回 query type）
    return this.currentItem ? this.currentItem.type === '1' : this.type === '1';
  }

  // ============ 滑轨 visibleSlots 计算 ============

  // 返回 3 个 slot：[prev, current, next]
  // 用 videoUrl 作为 key，Vue 的 v-for 会按 key 复用 DOM，
  // 所以无论列表有多长，始终只有 3 个 <video> 元素存在
  get visibleSlots(): TrackSlot[] {
    const slots: TrackSlot[] = [];
    const positions: SlotPosition[] = ['prev', 'current', 'next'];
    const indices: (number | null)[] = [
      this.currentIndex - 1,
      this.currentIndex,
      this.currentIndex + 1,
    ];

    indices.forEach((idx, i) => {
      if (idx !== null && idx >= 0 && idx < this.playList.length) {
        slots.push({
          key: this.playList[idx].videoUrl,
          item: this.playList[idx],
          position: positions[i],
        });
      } else {
        // 边界：首/尾缺失，用空 key 占位（此 slot 不渲染 video-box）
        slots.push({
          key: `empty_${positions[i]}`,
          item: null as any,
          position: positions[i],
        });
      }
    });

    return slots;
  }

  // 滑轨 transform：基础位置 + 实时拖动
  // trackSlot: 0=显示prev, 1=显示current(默认), 2=显示next
  // 3个slide垂直排列，需要 translateY(-slotIndex * 100%) 来显示对应slide
  get trackStyle(): { transform: string } {
    const basePercent = -this.trackSlot * 100;
    // dragPercent 是相对 video-track 自身高度（= video-area 高度）的百分比
    const areaHeight = this.getVideoAreaHeight();
    const dragPercent = areaHeight > 0 ? (this.liveDragY / areaHeight) * 100 : 0;
    return { transform: `translateY(calc(${basePercent}% + ${dragPercent}%))` };
  }

  // 获取 video-area 元素的高度（用于拖动百分比换算）
  getVideoAreaHeight(): number {
    const el = (this.$el as HTMLElement)?.querySelector('.video-area') as HTMLElement;
    return el ? el.offsetHeight : window.innerHeight;
  }

  created() {
    this.isNewYear = localStorage.getItem('theme') === 'New_Year';
    this.theme = localStorage.getItem('theme') || '';

    const url = this.$route.query.videoUrl;
    if (typeof url === 'string') this.videoUrl = url;
    const coverUrl = this.$route.query.coverUrl;
    if (typeof coverUrl === 'string') this.coverUrl = coverUrl;
    const type = this.$route.query.type;
    if (typeof type === 'string') this.type = type;
    const taskId = this.$route.query.taskId;
    if (typeof taskId === 'string') this.taskId = taskId;
    const articleId = this.$route.query.articleId;
    if (typeof articleId === 'string') this.articleId = articleId;
    const swipeable = this.$route.query.swipeable;
    this.swipeable = swipeable === '1';

    // 启用滑动切换时加载完整列表；否则只把传入的视频设进播放列表（滑轨渲染依赖 playList，
    // 不设置的话 visibleSlots 为空，传入的视频无法播放）
    if (this.swipeable) {
      this.loadPlayList();
    } else {
      this.playList = [this.buildInitialItem()];
      this.currentIndex = 0;
    }
  }

  // 用 query 传入的参数构造单个播放项
  buildInitialItem(): PlayListItem {
    return {
      videoUrl: this.videoUrl,
      coverUrl: this.coverUrl,
      articleId: this.articleId,
      description: '',
      styleEN: '',
      speakerSpeed: '',
      speakerName: '',
      speakerStyle: '',
      type: this.type,
    };
  }

  loadPlayList() {
    const initialItem: PlayListItem = this.buildInitialItem();

    let templateList: PlayListItem[] = [];
    const raw = localStorage.getItem('videoPlayList');
    if (raw) {
      try {
        templateList = JSON.parse(raw) || [];
      } catch (e) {
        templateList = [];
      }
    }
    const matchIdx = templateList.findIndex(v => v.videoUrl === this.videoUrl);
    if (matchIdx >= 0) {
      templateList[matchIdx].type = '2';
      this.playList = templateList;
      this.currentIndex = matchIdx;
    } else {
      this.playList = [initialItem, ...templateList];
      this.currentIndex = 0;
    }
  }

  mounted() {
    if (this.type === '2') {
      document.title = '视频播放';
    }
    // 从咪咕 taskId 页返回：消费 taskId 并直接触发提交
    this.setTaskId();
    // 从发音人搜索页返回：恢复抽屉打开状态和草稿
    this.restoreCreateDrawer();
    this.$nextTick(() => {
      this.playCurrentVideo();
      // 首次进入显示滑动提示蒙层，2秒后关闭（仅开启滑动切换时）
      if (this.swipeable && this.playList.length > 1) {
        this.showSwipeHint = true;
        this.swipeHintTimer = setTimeout(() => {
          this.showSwipeHint = false;
        }, 2000);
      }
    });
  }

  beforeDestroy() {
    clearTimeout(this.playIconTimer);
    clearTimeout(this.bubbleTimer);
    clearTimeout(this.swipeHintTimer);
    this.pauseAllVideos();
  }

  // ============ 视频播放管理 ============

  // 获取当前 slot 的 video 元素
  getCurrentVideoElement(): HTMLVideoElement | null {
    const track = this.$refs.videoTrack as HTMLElement;
    if (!track) return null;
    // current slot 是第 2 个 video-slide
    const slides = track.querySelectorAll('.video-slide');
    const currentSlide = slides[1];
    if (!currentSlide) return null;
    const video = currentSlide.querySelector('video');
    return video as HTMLVideoElement | null;
  }

  pauseAllVideos() {
    const track = this.$refs.videoTrack as HTMLElement;
    if (!track) return;
    track.querySelectorAll('video').forEach(v => v.pause());
  }

  // 进度条：监听当前视频 timeupdate
  bindVideoProgress(video: HTMLVideoElement) {
    // 元数据加载后拿总时长
    if (video.duration > 0 && !isNaN(video.duration)) {
      this.videoDuration = video.duration;
    }
    video.ontimeupdate = () => {
      // 拖动中由拖动逻辑接管进度，不回写（避免进度回跳）
      if (this.isDraggingProgress) return;
      this.videoCurrentTime = video.currentTime;
      if (video.duration > 0 && !isNaN(video.duration)) {
        this.videoDuration = video.duration;
      }
      this.videoProgress = this.videoDuration > 0
        ? video.currentTime / this.videoDuration
        : 0;
    };
  }

  // ============ 进度条拖动 ============

  // 由触摸点算进度比例（clamp 0~1）
  getProgressRatioFromTouch(e: TouchEvent): number | null {
    const track = e.currentTarget as HTMLElement;
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const x = e.touches[0]?.clientX ?? (e as any).changedTouches?.[0]?.clientX;
    if (x == null) return null;
    return Math.min(1, Math.max(0, (x - rect.left) / rect.width));
  }

  onProgressTouchStart(e: TouchEvent) {
    if (this.switching || this.videoDuration <= 0) return;
    const ratio = this.getProgressRatioFromTouch(e);
    if (ratio == null) return;
    this.isDraggingProgress = true;
    this.dragProgressRatio = ratio;
    this.videoProgress = ratio; // 按下即跳到该位置
    this.videoCurrentTime = ratio * this.videoDuration;
  }

  onProgressTouchMove(e: TouchEvent) {
    if (!this.isDraggingProgress) return;
    const ratio = this.getProgressRatioFromTouch(e);
    if (ratio == null) return;
    this.dragProgressRatio = ratio;
    this.videoProgress = ratio;
    this.videoCurrentTime = ratio * this.videoDuration;
  }

  onProgressTouchEnd() {
    if (!this.isDraggingProgress) return;
    this.isDraggingProgress = false;
    const video = this.getCurrentVideoElement();
    if (video && this.dragProgressRatio >= 0) {
      // 拖动结束 seek 到目标位置；已暂停则只更新画面到该帧
      video.currentTime = this.dragProgressRatio * this.videoDuration;
    }
  }

  resetVideoProgress() {
    this.videoProgress = 0;
    this.videoCurrentTime = 0;
    this.videoDuration = 0;
    this.isDraggingProgress = false;
    this.dragProgressRatio = 0;
  }

  // 秒 → mm:ss
  formatTime(seconds: number): string {
    if (!seconds || !isFinite(seconds) || isNaN(seconds)) return '00:00';
    const total = Math.floor(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
  }

  get videoCurrentTimeText() {
    return this.formatTime(this.videoCurrentTime);
  }
  get videoDurationText() {
    return this.formatTime(this.videoDuration);
  }

  playCurrentVideo() {
    const video = this.getCurrentVideoElement();
    if (!video) return;

    video.muted = this.isMuted;
    video.currentTime = 0;
    this.resetVideoProgress();
    this.bindVideoProgress(video);

    video.play().then(() => {
      this.isPlaying = true;

      // 播放图标只在首次进入时显示，切换视频不再弹出
      if (this._isFirstPlay) {
        this._isFirstPlay = false;
        this.showPlayIcon = true;
        this.scheduleHidePlayIcon();
      }

      if (!this._bubbleShown) {
        this._bubbleShown = true;
        this.showBubble = true;
        this.bubbleTimer = setTimeout(() => {
          this.showBubble = false;
        }, 2000);
      }

      video.onended = () => {
        this.isPlaying = false;
      };
    }).catch(() => {});
  }

  togglePlay() {
    if (this.switching) return;
    const video = this.getCurrentVideoElement();
    if (!video) return;

    if (this.isPlaying) {
      video.pause();
      this.isPlaying = false;
      clearTimeout(this.playIconTimer);
      this.showPlayIcon = true;
    } else {
      video.play();
      this.isPlaying = true;
      this.showPlayIcon = true;
      this.scheduleHidePlayIcon();
    }
  }

  scheduleHidePlayIcon() {
    clearTimeout(this.playIconTimer);
    this.playIconTimer = setTimeout(() => {
      if (this.isPlaying) {
        this.showPlayIcon = false;
      }
    }, 1000);
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    const video = this.getCurrentVideoElement();
    if (video) video.muted = this.isMuted;
    this.showBubble = false;
    clearTimeout(this.bubbleTimer);
  }

  // ============ 上下滑动切换 ============

  onTouchStart(e: TouchEvent) {
    if (this.switching || !this.swipeable) return;
    this.touchStartY = e.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isSwiping = true;
    this.liveDragY = 0;
    this.noTransition = true; // 拖动时关闭动画
  }

  onTouchMove(e: TouchEvent) {
    if (this.swipeable && this.isSwiping && this.playList.length > 1) {
      e.preventDefault();
      const currentY = e.touches[0].clientY;
      this.liveDragY = currentY - this.touchStartY;
    }
  }

  onTouchEnd(e: TouchEvent) {
    if (!this.swipeable || !this.isSwiping || this.switching) {
      this.isSwiping = false;
      this.liveDragY = 0;
      return;
    }
    this.isSwiping = false;
    this.noTransition = false; // 恢复动画

    if (this.playList.length <= 1) {
      this.liveDragY = 0;
      return;
    }

    const endY = e.changedTouches[0].clientY;
    const deltaY = this.touchStartY - endY; // 正 = 上滑(下一个), 负 = 下滑(上一个)
    this.liveDragY = 0;

    if (Math.abs(deltaY) < this.slideThreshold) {
      // 滑动距离不够，回弹
      return;
    }

    if (deltaY > 0) {
      this.switchTo(this.currentIndex + 1, 'next');
    } else {
      this.switchTo(this.currentIndex - 1, 'prev');
    }
  }

  // direction: 'next' = 上滑到下一个, 'prev' = 下滑到上一个
  switchTo(targetIdx: number, direction: 'next' | 'prev') {
    if (this.switching) return;

    // 边界检查
    if (targetIdx < 0 || targetIdx >= this.playList.length) {
      this.$toast({ message: '没有更多了～', duration: 2000 });
      return;
    }

    this.switching = true;

    // 暂停当前视频
    const currentVideo = this.getCurrentVideoElement();
    if (currentVideo) {
      currentVideo.pause();
      currentVideo.ontimeupdate = null; // 清除旧进度监听
    }

    // 播放图标隐藏
    this.isPlaying = false;
    this.showPlayIcon = false;
    this.resetVideoProgress();

    // 第 1 步：trackSlot 动画到目标位置
    // 上滑 → slot 2 (next)；下滑 → slot 0 (prev)
    this.trackSlot = direction === 'next' ? 2 : 0;

    // 第 2 步：等动画结束后（350ms），执行 snap-back
    setTimeout(() => {
      // 关闭 transition
      this.noTransition = true;

      // 更新 currentIndex（visibleSlots 随之变化）
      this.currentIndex = targetIdx;

      // 重置 trackSlot 回到 1（中间）
      this.trackSlot = 1;

      // 等 Vue DOM 更新后播放新视频
      this.$nextTick(() => {
        this.playCurrentVideo();

        // 给浏览器一帧渲染时间再恢复 transition
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.noTransition = false;
            this.switching = false;
          });
        });
      });
    }, 350);
  }

  // ============ 跳转 ============

  // 返回上一页
  goBack() {
    this.$router.back();
  }

  get currentVideoUrl() {
    return this.currentItem ? this.currentItem.videoUrl : this.videoUrl;
  }
  get currentCoverUrl() {
    return this.currentItem ? this.currentItem.coverUrl : this.coverUrl;
  }
  get currentArticleId() {
    return this.currentItem ? this.currentItem.articleId : this.articleId;
  }

  async toPublish() {
    const obj: any = {
      videoUrl: this.currentVideoUrl,
      coverUrl: this.currentCoverUrl,
    };
    if ((window as any)._amberTrack) {
      const vuid = localStorage.getItem('vuid') || '';
      (window as any)._amberTrack('music_aigc_publish_ringtone', [
        { EK: 'triggerType', EV: '2' },
        { EK: 'scene', EV: MiguConfig.projectId },
        { EK: 'videoUrl', EV: obj.videoUrl },
        { EK: 'resId', EV: this.currentArticleId || '' },
        { EK: 'vuid', EV: vuid },
        { EK: 'appId', EV: MiguConfig.appid },
        { EK: 'channel', EV: MiguConfig.schannel }
      ], true, 'beacon');
      console.log('_amberTrack music_aigc_publish_ringtone',
        { EK: 'videoUrl', EV: obj.videoUrl },
        { EK: 'resId', EV: this.currentArticleId || '' },
        { EK: 'vuid', EV: vuid }
      );
    }
    setTimeout(async () => {
      await toPublishPage(obj);
    }, 200);
  } 

  // 我也试试：暂停当前视频并弹出创作抽屉（不再跳 /migu）
  toCreate() {
    // 播放中则先暂停
    this.pauseCurrentVideo();
    try {
      if ((window as any)._amberTrack) {
        const vuid = localStorage.getItem('vuid') || '';
        (window as any)._amberTrack('music_aigc_submit_task_directly', [
          { EK: 'triggerType', EV: '2' },
          { EK: 'scene', EV: MiguConfig.projectId },
          { EK: 'taskId', EV: this.taskId },
          { EK: 'vuid', EV: vuid },
          { EK: 'appId', EV: MiguConfig.appid },
          { EK: 'channel', EV: MiguConfig.schannel }
        ], true, 'beacon');
        console.log('_amberTrack music_aigc_submit_task_directly',
          { EK: 'taskId', EV: this.taskId },
          { EK: 'vuid', EV: vuid }
        );
      }
    } catch (e) {}
    // 带入当前播放视频的创作参数（与 workTemplate.getSaveData 字段映射一致）
    const item = this.currentItem;
    if (item) {
      this.drawerText = item.description || '';
      this.drawerStyleCode = item.styleEN || '';
      this.drawerSpeakerName = item.speakerName || '';
      this.drawerSpeakerStyleName = item.speakerStyle || '';
      this.drawerSpeechSpeed = item.speakerSpeed || '';
      this.drawerSpeakerStyleId = 0; // 无风格id，由 styleName 在情绪列表中匹配
    }
    this.showCreateDrawer = true;
  }

  // 暂停当前视频并复位播放状态图标
  pauseCurrentVideo() {
    const video = this.getCurrentVideoElement();
    if (video && !video.paused) {
      video.pause();
      this.isPlaying = false;
    }
  }

  // ============ 创作抽屉配套（对齐 audioCreating 的使用方式） ============

  // 恢复创作抽屉（发音人搜索页选完发音人返回时 / taskId 失败返回时）
  restoreCreateDrawer() {
    const reopen = localStorage.getItem('createDrawerReopen');
    if (reopen !== 'true') return;
    localStorage.removeItem('createDrawerReopen');
    const data = JSON.parse(localStorage.getItem('createDrawerMiguData') || 'null');
    if (data) {
      this.drawerText = data.text || '';
      this.drawerStyleCode = data.styleCode || '';
      this.drawerSpeakerName = data.speakerName || '';
      this.drawerSpeakerStyleId = data.speakerStyleId || 0;
      this.drawerSpeakerStyleName = data.speakerStyle || '';
      this.drawerSpeechSpeed = data.speechSpeed || '';
    }
    this.showCreateDrawer = true;
  }

  // 登录校验
  checkLogin() {
    const commonPub = this.$store.getters['miguPage/getCommonPub'];
    if (commonPub) {
      const btoken = localStorage.getItem('btoken');
      if (!btoken) {
        // videoPlay 无登录弹窗组件，跳咪咕登录页
        this.doLogin();
        return false;
      }
    }
    return true;
  }

  // 提交前验证（对齐 textToVideo.beforeSumitVerify：基础校验 + 上一个任务状态检查）
  async beforeSubmitVerify(params: any) {
    if (!params.originContent) {
      toast('请输入文案');
      return false;
    }
    if (params.originContent.length < 35) {
      toast('请输入至少35个字');
      return false;
    }
    if (!params.speaker) {
      toast('请选择发音人');
      return false;
    }
    // 上一个任务未完成（非失败/成功导出终态）时，不允许重复提交
    const lastArticleId = localStorage.getItem('miguLastArticleId');
    if (lastArticleId) {
      const [error, res] = await to(this.$http('getArticleStatus', { articleId: lastArticleId }));
      if (!error && res.code === 200 && res.data) {
        const status = res.data.status; // 5失败, 6成功
        const lastExportTaskStatus = res.data.lastExportTaskStatus; // 2成功,3/4失败
        if (status === 5 || (status === 6 && [2, 3, 4].includes(lastExportTaskStatus))) {
          localStorage.removeItem('miguLastArticleId');
        } else {
          toast('已有处理中的任务, 请稍后再次提交');
          return false;
        }
      }
    }
    return true;
  }

  // 抽屉提交（对齐 textToVideo.doSubmit：async 验证 + await getMiguToken）
  async onDrawerSubmit(params: any) {
    if (this.isSubmiting) return;
    if (!this.checkLogin()) return;
    const verify = await this.beforeSubmitVerify(params);
    if (!verify) return;
    this.isSubmiting = true;

    // 补全提交参数
    params.creationTaskId = this.taskId;
    params.title = params.originContent.slice(0, 80);
    params.platform = 'MIGU';
    params.resourceType = 'ONE_CLICK';
    params.articleTransform = 0;

    try {
      if ((window as any).getMiguToken) {
        const rcToken = await (window as any).getMiguToken();
        params.rcToken = rcToken;
      }
    } catch (e) {
      console.error('getMiguToken error', e);
    }

    this.doSubmitRequest(params);
  }

  doSubmitRequest(params: any) {
    params.channelCode = MiguConfig.schannel;
    this.$http('submitCreate', params).then((res: any) => {
      this.isSubmiting = false;
      if (res.code == 200) {
        this.isSubmiting = true;
        try {
          if ((window as any)._amberTrack) {
            localStorage.setItem('creationTaskIdMigu', params.creationTaskId + '');
            const vuid = localStorage.getItem('vuid') || '';
            (window as any)._amberTrack('music_aigc_submit_task', [
              { EK: 'triggerType', EV: '2' },
              { EK: 'scene', EV: MiguConfig.projectId },
              { EK: 'taskId', EV: params.creationTaskId },
              { EK: 'vuid', EV: vuid },
              { EK: 'appId', EV: MiguConfig.appid },
              { EK: 'channel', EV: MiguConfig.schannel }
            ], true, 'beacon');
          }
        } catch (e) {}
        localStorage.setItem('miguLastArticleId', res.data.articleId + '');
        this.showCreateDrawer = false;
        this.$router.push({ path: '/audioCreating', query: { articleId: res.data.articleId, source: 'app_migu' } });
      } else if (res.message == notLoginMessage) {
        this.doLogin();
      } else {
        toast(res.message);
      }
    }).catch((err: any) => {
      this.isSubmiting = false;
      if (err?.response?.data?.message === notLoginErrorMessage) {
        this.doLogin();
      }
    });
  }

  // 查询 taskId（跳转咪咕 taskId 页获取，回来后由 setTaskId 触发提交）
  async onDrawerQueryTask() {
    if (this.taskId) {
      // 已有 taskId，直接提交
      const drawerRef = this.$refs.createDrawerRef as any;
      if (drawerRef) {
        drawerRef.doCreateSubmit();
      }
      return;
    }
    if (!this.checkLogin()) return;

    // 跳转前校验参数，避免空参数白跳一趟
    const drawerRef = this.$refs.createDrawerRef as any;
    const submitParams = drawerRef ? drawerRef.submitParams : {};
    const verify = await this.beforeSubmitVerify(submitParams);
    if (!verify) return;

    this.setLocalStorageData(); // 草稿回显（回来后恢复抽屉用）
    // 存提交标记 + 提交参数，跳转回来后 setTaskId 直接触发提交
    localStorage.setItem('isMiguSubmit', 'true');
    localStorage.setItem('videoPlayMiguSubmitData', JSON.stringify(submitParams));
    const cburl = encodeURIComponent(window.location.href.split('?')[0]);
    localStorage.setItem('miguDidLogin', '1');

    // 获取 btoken
    const btoken = localStorage.getItem('btoken') || '';

    // 根据 commonPub 标识决定使用哪个 URL（taskId 专用页，与 audioCreating/works 一致）
    const commonPub = this.$store.getters['miguPage/getCommonPub'];
    if (commonPub) {
      let url = `https://y.migu.cn/app/v5/p/middle/ai-channel-task-id/index.html?appId=${MiguConfig.appid}&projectId=${MiguConfig.projectId}&cburl=${cburl}&schannel=${MiguConfig.schannel}`;
      if (btoken) {
        url += `&btoken=${btoken}`;
      }
      url += '&pm=hash#/task-id';
      window.location.href = url;
    } else {
      window.location.href = `https://y.migu.cn/app/v5/p/ai-charging/index.html?appId=${MiguConfig.appid}&projectId=${MiguConfig.projectId}&cburl=${cburl}&schannel=${MiguConfig.schannel}&pm=hash#/task-id`;
    }
  }

  // 设置taskId（从咪咕 taskId 页返回后触发提交，对齐 audioCreating 的 setTaskId）
  setTaskId() {
    const isMiguSubmit = localStorage.getItem('isMiguSubmit');
    if (!isMiguSubmit) return; // 不是提交过来的，不去取taskId
    const query = this.$route.query;
    const taskId = query.taskId || '';
    if (typeof taskId === 'string') this.taskId = taskId; // 空值不覆盖已有值
    if (!taskId) {
      const resumeCode = query.resumeCode || '';
      // 用户支付状态码0：用户支付失败, 1：用户支付成功, 2：用户取消支付, 3：用户已有次数, 4：获取taskId获取失败
      const error = query.info as string;
      if (resumeCode === '4') {
        let msg = error || '获取taskId失败';
        if (msg.includes('存在进行中的任务')) msg = '存在进行中的任务';
        toast(msg, 'middle', 5000);
      } else if (resumeCode === '2') {
        toast(error || '您已取消支付');
      } else if (resumeCode === '0') {
        toast(error || '支付失败');
      }
      // 失败/取消：清标记并恢复抽屉，用户可修改后重试
      localStorage.removeItem('isMiguSubmit');
      localStorage.setItem('createDrawerReopen', 'true');
      this.restoreCreateDrawer();
      return;
    }
    // 有taskId：读取跳转前存的提交参数，直接触发提交
    const data = JSON.parse(localStorage.getItem('videoPlayMiguSubmitData') || 'null');
    localStorage.removeItem('isMiguSubmit');
    if (!data) return;
    data.creationTaskId = taskId;
    this.onDrawerSubmit(data);
  }

  // 保存抽屉草稿（发音人搜索页跳转 / taskId 页跳转前调用）
  setLocalStorageData() {
    const drawerRef = this.$refs.createDrawerRef as any;
    if (drawerRef) {
      const params = drawerRef.saveParams;
      // 存到抽屉专用 key，与发音人搜索页 speakerInfo 的读写保持一致
      localStorage.setItem('createDrawerMiguData', JSON.stringify(params));
      // 同步到页面 state，抽屉销毁重建（v-if）后重开可回显
      this.drawerText = params.text || '';
      this.drawerStyleCode = params.styleCode || '';
      this.drawerSpeakerName = params.speakerName || '';
      this.drawerSpeakerStyleId = params.speakerStyleId || 0;
      this.drawerSpeakerStyleName = params.speakerStyle || '';
      this.drawerSpeechSpeed = params.speechSpeed || '';
    }
  }

  // 登录（跳咪咕登录页）
  doLogin() {
    this.showCreateDrawer = false;
    this.setLocalStorageData();
    localStorage.setItem('miguDidLogin', '1');
    const cburl = encodeURIComponent(window.location.href.split('?')[0]);
    const btoken = localStorage.getItem('btoken') || '';
    const commonPub = this.$store.getters['miguPage/getCommonPub'];
    if (commonPub) {
      let url = `https://y.migu.cn/app/v5/p/middle/ai-channel-token/index.html?appId=${MiguConfig.appid}&schannel=${MiguConfig.schannel}&projectId=${MiguConfig.projectId}&cburl=${cburl}&pm=hash&cToken=${btoken}`;
      window.location.href = url;
    } else {
      window.location.href = `https://y.migu.cn/app/v5/p/ai-login/index.html?appId=${MiguConfig.appid}&schannel=${MiguConfig.schannel}&projectId=${MiguConfig.projectId}&cburl=${cburl}`;
    }
  }

  // 跳转付费
  doPay() {
    this.setLocalStorageData();
    const cburl = encodeURIComponent(window.location.href.split('?')[0]);
    localStorage.setItem('miguDidLogin', '1');
    window.location.href = `https://y.migu.cn/app/v5/p/ai-pay/index.html?appId=${MiguConfig.appid}&projectId=${MiguConfig.projectId}&cburl=${cburl}&schannel=${MiguConfig.schannel}&pm=hash`;
  }

  // 显示订购弹窗（对齐 textToVideo：无权益时弹出 subscribe-popup）
  showSubscribe() {
    this.showSubscribePopup = true;
  }

  // 关闭订购弹窗
  closeSubscribe() {
    this.showSubscribePopup = false;
  }

  // ============ 协议弹窗（抽屉勾选框点协议名触发） ============

  // 勾选框点协议名：关闭抽屉（若打开），弹协议弹窗
  onShowAgreement() {
    this.drawerOpenBeforeAgreement = this.showCreateDrawer;
    if (this.showCreateDrawer) {
      this.showCreateDrawer = false;
    }
    this.showAgreementPopup = true;
  }

  // 协议弹窗点「我已阅读并同意」：自动勾选抽屉协议
  onAgreementAgree() {
    this.drawerAgreed = true;
  }

  // 关闭协议弹窗：恢复协议弹出前的抽屉打开状态
  onAgreementClose() {
    this.showAgreementPopup = false;
    if (this.drawerOpenBeforeAgreement) {
      this.showCreateDrawer = true;
      this.drawerOpenBeforeAgreement = false;
    }
  }
}
